import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { PartnerStore } from '@app/config/partner-store';
import { Store } from '@app/config/store';
import { password, passwordMatchValidator } from '@app/dto/common.dto';
import { AdminAuthService } from '@app/services/admin-auth.service';
import { AuthService, ChangePassword } from '@app/services/auth.service';
import { EncryptionService } from '@app/services/encrytion.service';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { matRemoveRedEye, matVisibilityOff } from '@ng-icons/material-icons/baseline';

@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgIconComponent],
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.css',
  providers: [
    provideIcons({
      matRemoveRedEye,
      matVisibilityOff,
    }),
  ],
})
export class ChangePasswordComponent {
  @Output() closeEvent = new EventEmitter<void>();
  @Input() showCancelButton: boolean = true;
  @Input() token: string = '';
  @Input() helpText: string = '';

  viewPassword = false;
  loading = false;

  passwordForm = new FormGroup(
    {
      currentPassword: new FormControl(''),
      newPassword: new FormControl('', [password]),
      confirmNewPassword: new FormControl('', [password]),
    },
    { validators: passwordMatchValidator() },
  );

  constructor(
    private authService: AuthService,
    private partnerStore: PartnerStore,
    private store: Store,
    private adminAuthService: AdminAuthService,
    private encrypt: EncryptionService,
  ) {}

  async savePassword() {
    if (this.loading) return;
    this.loading = true;

    try {
      const formValue = this.passwordForm.value;
      const current = (formValue.currentPassword ?? '') as string;
      const newPwd = (formValue.newPassword ?? '') as string;
      const confirmPwd = (formValue.confirmNewPassword ?? '') as string;

      const [encryptedCurrentPassword, encryptedNewPassword, encryptedConfirmNewPassword] =
        await Promise.all([
          this.encrypt.encryptWithPublicKey(current),
          this.encrypt.encryptWithPublicKey(newPwd),
          this.encrypt.encryptWithPublicKey(confirmPwd),
        ]);

      const changePassword: ChangePassword & { confirmNewPassword?: string } = {
        currentPassword: encryptedCurrentPassword,
        newPassword: encryptedNewPassword,
        confirmNewPassword: encryptedConfirmNewPassword,
      };

      // If token provided, temporarily set it in store for the request
      if (this.token) this.store.authToken.set(this.token);

      const isAdmin = Boolean(this.partnerStore.partnerAuth() && !this.store.authToken());
      if (isAdmin) {
        this.adminAuthService.changePassword(changePassword).subscribe(
          (resp) => {
            if (resp.status) this.closeEvent.emit();
          },
          (err) => {
            console.error('Admin changePassword error', err);
          },
          () => {
            if (this.token) this.store.authToken.set(null);
          },
        );
      } else {
        this.authService.changePassword(changePassword).subscribe(
          (resp) => {
            if (resp.status) this.closeEvent.emit();
            else if (this.token) this.store.authToken.set(null);
          },
          (err) => {
            console.error('changePassword error', err);
            if (this.token) this.store.authToken.set(null);
          },
        );
      }
    } catch (err) {
      console.error('Encryption / change password failed:', err);
    } finally {
      this.loading = false;
    }
  }

  cancel(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    this.passwordForm.reset();
    this.closeEvent.emit();
  }
}
